import { IUser } from "../interfaces/user.interface";
import { PERMISSIONS } from "../hooks/usePermissions";

export interface RBACConfig {
  role: string;
  permissions: string[];
  dataScope: "enterprise" | "federation" | "transporter" | "self";
}

// RBAC Configuration for different roles
export const RBAC_CONFIGS: Record<string, RBACConfig> = {
  ROLE_CLIENT_ADMIN: {
    role: "ROLE_CLIENT_ADMIN",
    permissions: [
      PERMISSIONS.CAN_CREATE_JOB,
      PERMISSIONS.CAN_EDIT_JOB,
      PERMISSIONS.CAN_DELETE_JOB,
      PERMISSIONS.CAN_CLOSE_JOB,
      PERMISSIONS.CAN_TRACK_JOB,
      PERMISSIONS.CAN_CREATE_USER,
      PERMISSIONS.CAN_CRUD_TRANSPORTER,
      PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER,
      PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
      PERMISSIONS.CAN_CREATE_FEDERATION,
    ],
    dataScope: "enterprise",
  },
  ROLE_FEDERATION_ADMIN: {
    role: "ROLE_FEDERATION_ADMIN",
    permissions: [
      PERMISSIONS.CAN_CREATE_JOB,
      PERMISSIONS.CAN_EDIT_JOB,
      PERMISSIONS.CAN_DELETE_JOB,
      PERMISSIONS.CAN_CLOSE_JOB,
      PERMISSIONS.CAN_TRACK_JOB,
      PERMISSIONS.CAN_CREATE_USER,
      PERMISSIONS.CAN_CRUD_TRANSPORTER,
      PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER,
      PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
    ],
    dataScope: "federation",
  },
  ROLE_TRANSPORTER_ADMIN: {
    role: "ROLE_TRANSPORTER_ADMIN",
    permissions: [
      PERMISSIONS.CAN_CREATE_JOB,
      PERMISSIONS.CAN_EDIT_JOB,
      PERMISSIONS.CAN_DELETE_JOB,
      PERMISSIONS.CAN_CLOSE_JOB,
      PERMISSIONS.CAN_TRACK_JOB,
      PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER,
      PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
    ],
    dataScope: "transporter",
  },
};

// Utility functions for RBAC
export const rbacUtils = {
  // Get user's role configuration
  getUserRoleConfig: (user: IUser): RBACConfig | null => {
    const roleName = user?.role?.name;
    return roleName ? RBAC_CONFIGS[roleName] || null : null;
  },

  // Check if user has specific permission
  hasPermission: (user: IUser, permission: string): boolean => {
    const roleConfig = rbacUtils.getUserRoleConfig(user);
    return roleConfig?.permissions.includes(permission) || false;
  },

  // Check if user has any of the specified permissions
  hasAnyPermission: (user: IUser, permissions: string[]): boolean => {
    const roleConfig = rbacUtils.getUserRoleConfig(user);
    return (
      roleConfig?.permissions.some((permission) =>
        permissions.includes(permission)
      ) || false
    );
  },

  // Check if user has all of the specified permissions
  hasAllPermissions: (user: IUser, permissions: string[]): boolean => {
    const roleConfig = rbacUtils.getUserRoleConfig(user);
    return (
      roleConfig?.permissions.every((permission) =>
        permissions.includes(permission)
      ) || false
    );
  },

  // Get user's data scope
  getUserDataScope: (user: IUser): string => {
    const roleConfig = rbacUtils.getUserRoleConfig(user);
    return roleConfig?.dataScope || "self";
  },

  // Check if user can access specific resource type
  canAccessResource: (user: IUser, resourceType: string): boolean => {
    const roleConfig = rbacUtils.getUserRoleConfig(user);
    if (!roleConfig) return false;

    switch (resourceType) {
      case "federations":
        return rbacUtils.hasPermission(user, PERMISSIONS.CAN_CREATE_FEDERATION);
      case "transporters":
        return rbacUtils.hasPermission(user, PERMISSIONS.CAN_CRUD_TRANSPORTER);
      case "drivers":
        return rbacUtils.hasPermission(
          user,
          PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER
        );
      case "trucks":
        return rbacUtils.hasPermission(
          user,
          PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER
        );
      case "jobs":
        return rbacUtils.hasAnyPermission(user, [
          PERMISSIONS.CAN_CREATE_JOB,
          PERMISSIONS.CAN_EDIT_JOB,
          PERMISSIONS.CAN_DELETE_JOB,
        ]);
      default:
        return false;
    }
  },

  // Get API endpoint based on user role
  getRoleBasedEndpoint: (user: IUser, baseEndpoint: string): string => {
    const roleName = user?.role?.name;

    switch (roleName) {
      case "ROLE_CLIENT_ADMIN":
        return `/enterprise${baseEndpoint}`;
      case "ROLE_FEDERATION_ADMIN":
        return `/fed${baseEndpoint}`;
      case "ROLE_TRANSPORTER_ADMIN":
        return `/transporter${baseEndpoint}`;
      default:
        return baseEndpoint;
    }
  },

  // Get user's organization type
  getUserOrgType: (user: IUser): string => {
    return user?.org?.orgType || "UNKNOWN";
  },

  // Check if user is enterprise admin
  isEnterpriseAdmin: (user: IUser): boolean => {
    return user?.role?.name === "ROLE_CLIENT_ADMIN";
  },

  // Check if user is federation admin
  isFederationAdmin: (user: IUser): boolean => {
    return user?.role?.name === "ROLE_FEDERATION_ADMIN";
  },

  // Check if user is transporter admin
  isTransporterAdmin: (user: IUser): boolean => {
    return user?.role?.name === "ROLE_TRANSPORTER_ADMIN";
  },

  // Get dashboard metrics based on user role
  getDashboardMetrics: (user: IUser): string[] => {
    const roleName = user?.role?.name;

    switch (roleName) {
      case "ROLE_CLIENT_ADMIN":
        return [
          "Total Federations",
          "Active Transporters",
          "Total Jobs",
          "Active Trips",
          "Completed Trips",
          "Total Users",
        ];
      case "ROLE_FEDERATION_ADMIN":
        return [
          "Total Transporters",
          "Active Drivers",
          "Total Jobs",
          "Active Trips",
          "Completed Trips",
        ];
      case "ROLE_TRANSPORTER_ADMIN":
        return [
          "Total Trucks",
          "Active Trucks",
          "Total Drivers",
          "Active Trips",
          "Completed Trips",
        ];
      default:
        return [];
    }
  },

  // Get quick actions based on user role and permissions
  getQuickActions: (
    user: IUser
  ): Array<{
    name: string;
    path: string;
    icon: string;
    color: string;
  }> => {
    const actions = [];

    // Job-related actions
    if (rbacUtils.hasPermission(user, PERMISSIONS.CAN_CREATE_JOB)) {
      actions.push({
        name: "Create New Job",
        path: "/jobs/create-new",
        icon: "📋",
        color: "#1062b9",
      });
    }

    if (
      rbacUtils.hasAnyPermission(user, [
        PERMISSIONS.CAN_CREATE_JOB,
        PERMISSIONS.CAN_EDIT_JOB,
        PERMISSIONS.CAN_DELETE_JOB,
      ])
    ) {
      actions.push({
        name: "View All Jobs",
        path: "/jobs/all",
        icon: "📊",
        color: "#28a745",
      });
    }

    // Transporter-related actions
    if (rbacUtils.hasPermission(user, PERMISSIONS.CAN_CRUD_TRANSPORTER)) {
      actions.push({
        name: "Manage Transporters",
        path: "/transporters",
        icon: "🚛",
        color: "#ffc107",
      });
    }

    // Driver-related actions
    if (
      rbacUtils.hasPermission(
        user,
        PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER
      )
    ) {
      actions.push({
        name: "Manage Drivers",
        path: "/drivers",
        icon: "👤",
        color: "#17a2b8",
      });
    }

    // Truck-related actions
    if (
      rbacUtils.hasPermission(
        user,
        PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER
      )
    ) {
      actions.push({
        name: "Manage Trucks",
        path: "/trucks",
        icon: "🚚",
        color: "#6f42c1",
      });
    }

    // Federation-related actions (Enterprise Admin only)
    if (
      rbacUtils.isEnterpriseAdmin(user) &&
      rbacUtils.hasPermission(user, PERMISSIONS.CAN_CREATE_FEDERATION)
    ) {
      actions.push({
        name: "Manage Federations",
        path: "/federations",
        icon: "🏢",
        color: "#fd7e14",
      });
    }

    return actions;
  },
};

export default rbacUtils;
